import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCalculations, deleteCalculation } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const fmt = (v, d=2) => v == null ? '—' : parseFloat(v).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});

const COUNTRY_COLOURS = {
  'Saudi Arabia': { bg:'#fef3c7', color:'#92400e' },
  'India':        { bg:'#dbeafe', color:'#1e40af' },
  'Australia':    { bg:'#d1fae5', color:'#065f46' },
};

export default function CalculationsHistoryPage() {
  const { isAdmin } = useAuth();
  const navigate    = useNavigate();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    getCalculations()
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRevisit = (row) => {
    // Navigate to calculator with state so it can pre-populate
    navigate('/calculator', { state: { prefill: row } });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this calculation record?')) return;
    setDeleting(id);
    try {
      await deleteCalculation(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(null); }
  };

  const filtered = rows.filter(r =>
    r.client_name.toLowerCase().includes(search.toLowerCase()) ||
    r.country.toLowerCase().includes(search.toLowerCase()) ||
    (r.user?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 className="page-title">🕘 Saved Quotes</h1>
        <p className="page-sub">
          {isAdmin ? 'All users\' saved quotes — latest per client' : 'Your saved quotes — latest per client'}
        </p>
      </div>

      {/* Search + count */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, gap:12, flexWrap:'wrap' }}>
        <input
          className="form-control"
          type="text"
          placeholder="Search by client, country or user..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth:340 }}
        />
        <span style={{ fontSize:13, color:'var(--text2)', flexShrink:0 }}>
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding:'60px 0', textAlign:'center', color:'var(--muted)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:'60px 0', textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
            <div style={{ fontSize:15, fontWeight:600, color:'var(--text2)' }}>No saved quotes yet</div>
            <div style={{ fontSize:13, color:'var(--muted)', marginTop:6 }}>Run a calculation from the Calculator page to save it here</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {isAdmin && <th>User</th>}
                  <th>Client</th>
                  <th>Country</th>
                  <th>Contractor Type</th>
                  <th>Pay Rate</th>
                  <th>Invoice Post Tax</th>
                  <th>Multiplier</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const bd  = row.result?.breakdown;
                  const res = row.result || {};
                  const inp = row.inputs || {};
                  const cc  = COUNTRY_COLOURS[row.country] || { bg:'#f1f5f9', color:'#475569' };
                  return (
                    <tr key={row.id}>
                      {isAdmin && (
                        <td style={{ fontSize:13 }}>
                          <div style={{ fontWeight:500 }}>{row.user?.name || '—'}</div>
                          <div style={{ fontSize:11, color:'var(--muted)' }}>{row.user?.email}</div>
                        </td>
                      )}
                      <td style={{ fontWeight:600, color:'var(--text)' }}>{row.client_name}</td>
                      <td>
                        <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:500, background:cc.bg, color:cc.color }}>
                          {row.country}
                        </span>
                      </td>
                      <td style={{ fontSize:13, color:'var(--text2)' }}>{inp.contractorType || '—'}</td>
                      <td style={{ fontSize:13 }}>
                        {inp.inputCurrency || res.currency} {fmt(inp.billRate)}
                        <div style={{ fontSize:11, color:'var(--muted)' }}>{inp.payFrequency}</div>
                      </td>
                      <td style={{ fontWeight:600, color:'var(--text)' }}>
                        {res.currency} {fmt(bd?.invoicePostTax?.value)}
                        <div style={{ fontSize:11, color:'var(--muted)' }}>{res.billingFrequency}</div>
                      </td>
                      <td style={{ fontWeight:600, color:'var(--accent)' }}>
                        {res.multiplierPostTax}x
                      </td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>
                        {new Date(row.updatedAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                        <div style={{ fontSize:11, color:'var(--muted)' }}>
                          {new Date(row.updatedAt).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}
                        </div>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleRevisit(row)}
                            style={{ fontSize:12 }}>
                            ↩ Revisit
                          </button>
                          {isAdmin && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(row.id)}
                              disabled={deleting === row.id}
                              style={{ fontSize:12 }}>
                              {deleting === row.id ? '...' : '🗑'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}